import { z } from 'zod/v4';

/**
 * Mirrors the legacy `patchByIds` body schema
 * (`backend/src/2025-06/api-service/routes/schemas/v3/request/user.js`).
 * Unknown properties are silently stripped (zod's default object behaviour)
 * rather than rejected, matching the same legacy Fastify quirk documented on
 * `AdminUserPatchBodySchema` — the schema declared
 * `additionalProperties: false` but Fastify never actually enforced it.
 */
export const AdminUsersBulkPatchItemSchema = z.object({
  id: z.string().min(1),
  isBlocked: z.boolean().optional(),
});

export const AdminUsersBulkPatchBodySchema = z
  .array(AdminUsersBulkPatchItemSchema)
  .min(1);

export type AdminUsersBulkPatchBody = z.infer<
  typeof AdminUsersBulkPatchBodySchema
>;

/**
 * Row shape returned by the admin bulk-patch endpoint. Faithful pass-through
 * of the raw `users` row (same shape as `getUserByIdUnscopedDb` +
 * `transformUserRow`) — unlike `AdminUserPatchResponseSchema`, the legacy
 * `bulkBlockUnScoped` this ports never adds `role`/`enabledDevelopmentTags`.
 */
export const AdminUsersBulkPatchRowSchema = z.looseObject({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  primaryCompanyId: z.string().nullable(),
  langId: z.string().nullable(),
  isBlocked: z.boolean(),
  developmentTag: z.string().nullable(),
  avatarFileUrl: z.string().nullable(),
  originalAvatarFileUrl: z.string().nullable(),
  updatedAt: z.union([z.string(), z.date()]).nullable(),
});

export type AdminUsersBulkPatchRow = z.infer<
  typeof AdminUsersBulkPatchRowSchema
>;

export const AdminUsersBulkPatchResponseSchema = z.array(
  AdminUsersBulkPatchRowSchema,
);

export type AdminUsersBulkPatchResponse = z.infer<
  typeof AdminUsersBulkPatchResponseSchema
>;
